import esriConfig from "@arcgis/core/config";
import esriId from "@arcgis/core/identity/IdentityManager";
import { updateArcgisToken } from "../services/actions/map";

const TOKEN_REFRESH_GRACE_MS = 2 * 60 * 1000;
const refreshPromises: Record<string, Promise<void>> = {};
let interceptorAdded = false;

const getArcgisToken = (store: any, host: string) =>
  store.getState().map.arcgisTokens?.[host];

export const ensureIdentityTokenForResource = async (
  store: any,
  resourceUrl: string,
  options?: { refreshIfMissing?: boolean },
) => {
  const parsedUrl = new URL(resourceUrl, window.location.origin);
  const host = parsedUrl.host;
  const tokenFromState = getArcgisToken(store, host);
  const refreshIfMissing = options?.refreshIfMissing ?? true;
  const hasTokenInState = tokenFromState !== undefined;
  const now = Date.now();
  const expiresAt = tokenFromState?.expires || 0;

  if (!hasTokenInState && !refreshIfMissing) return undefined;

  const shouldRefreshToken =
    !tokenFromState?.token ||
    (expiresAt > 0 && expiresAt - now <= TOKEN_REFRESH_GRACE_MS);

  if (shouldRefreshToken) {
    if (!refreshPromises[host]) {
      refreshPromises[host] = Promise.resolve(
        store.dispatch(updateArcgisToken(host) as any),
      )
        .catch(() => undefined)
        .finally(() => {
          delete refreshPromises[host];
        });
    }
    await refreshPromises[host];
  }

  const freshToken = getArcgisToken(store, host);
  if (!freshToken?.token) return undefined;

  const server = `${parsedUrl.origin}${parsedUrl.pathname}`;
  esriId.registerToken({
    server,
    token: freshToken.token,
    expires: freshToken.expires,
    ssl: parsedUrl.protocol === "https:",
  });

  return freshToken.token;
};

export const ensureArcgisInterceptor = (store: any) => {
  if (interceptorAdded) return;

  esriConfig.request.interceptors?.push({
    before: async function (params) {
      try {
        await ensureIdentityTokenForResource(store, params.url, {
          refreshIfMissing: false,
        });
      } catch (e) {
        return;
      }
    },
  });

  interceptorAdded = true;
};
